import { ChevronDown } from 'lucide-react';

export interface FaqEntryProps {
  id: string;
  question: string;
  answer: string;
}

/**
 * One question/answer disclosure.
 *
 * Native `<details>`/`<summary>` rather than the Radix `Accordion` used in
 * admin: a visitor's browser never sends the URL fragment to the server, so
 * a `#faq-<id>` deep link can only ever be honoured client-side, and a
 * component that unmounts its closed panels until JavaScript runs would
 * fail "works before hydration" (docs/features.md §8) on that first paint.
 * `<details>` needs neither — closed content is already in the DOM, and the
 * browser scrolls a fragment link to it, open or not, with zero JavaScript.
 */
export function FaqEntry({ id, question, answer }: FaqEntryProps) {
  return (
    <details
      id={`faq-${id}`}
      className="group border-b border-border py-5 first:pt-0 last:border-b-0"
    >
      <summary
        className={[
          'flex cursor-pointer list-none items-center justify-between gap-4 marker:content-none',
          'font-semibold text-text transition-colors group-open:text-accent',
        ].join(' ')}
      >
        {question}
        <ChevronDown
          className="size-4 shrink-0 text-text-muted transition-transform duration-[--hs-duration-base] group-open:rotate-180 group-open:text-accent"
          aria-hidden="true"
        />
      </summary>
      {/*
        `answer` is rich-text HTML from the admin's TipTap editor, sanitised
        server-side on write with a strict allowlist (docs/security.md §6) —
        the same trust basis every other `body_html` field on the public site
        renders under (e.g. `ProjectSections`, the services and CMS pages).
        `{answer}` here would print `<h1>`/`<strong>`/etc. as literal text:
        React escapes plain children, it does not parse them as markup.
      */}
      <div className="hs-prose mt-3" dangerouslySetInnerHTML={{ __html: answer }} />
    </details>
  );
}
